import { useState, useEffect, useRef } from 'react'
import { Sparkles, TrendingUp, AlertCircle, Lightbulb, Target, Download, Database, Trash2, Eye, X, Clock, BookOpen, RefreshCw, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import api from '../lib/api'
import toast from 'react-hot-toast'

const GTMResearch = () => {
  const [topic, setTopic] = useState('')
  const [context, setContext] = useState('')
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('research') // 'research' or 'knowledge-base'
  const [knowledgeBase, setKnowledgeBase] = useState([])
  const [loadingKB, setLoadingKB] = useState(false)
  const [selectedResearch, setSelectedResearch] = useState(null)
  const pollingRef = useRef(null)

  useEffect(() => {
    // Always fetch on mount and when tab changes
    fetchKnowledgeBase()
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [activeTab])

  // Poll for pending/processing jobs
  useEffect(() => {
    const hasPendingJobs = knowledgeBase.some(r => r.status === 'pending' || r.status === 'processing')
    
    if (hasPendingJobs) {
      pollingRef.current = setInterval(() => {
        fetchKnowledgeBase(true) // silent refresh
      }, 3000)
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current)
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [knowledgeBase])

  const fetchKnowledgeBase = async (silent = false) => {
    if (!silent) setLoadingKB(true)
    try {
      const response = await api.get('/gtm/knowledge-base')
      setKnowledgeBase(response.data)
    } catch (err) {
      if (!silent) toast.error('Failed to load research tasks')
    } finally {
      if (!silent) setLoadingKB(false)
    }
  }

  const handleResearch = async (e) => {
    e.preventDefault()
    if (!topic.trim()) return

    setLoading(true)
    setError('')

    try {
      await api.post('/gtm/research', { topic, context, aiModel: selectedModel })
      toast.success('Research task started! You will be notified when complete.')
      setTopic('')
      setContext('')
      fetchKnowledgeBase()
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to start research'
      if (errorMsg.includes('quota') || errorMsg.includes('429') || errorMsg.includes('API_QUOTA_EXCEEDED')) {
        setError('⚠️ Gemini API quota exceeded (20 requests/day on free tier). Please wait until tomorrow or upgrade your plan.')
      } else {
        setError(errorMsg)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleExportPDF = async (item) => {
    if (!item) return
    
    try {
      toast.loading('Generating PDF...', { id: 'pdf-export' })
      const response = await api.get(`/gtm/knowledge-base/${item._id}/export/pdf`, {
        responseType: 'blob'
      })
      
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `GTM-Research-${item.topic.replace(/[^a-zA-Z0-9]/g, '-').substring(0, 30)}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('PDF exported successfully', { id: 'pdf-export' })
    } catch (err) {
      console.error('PDF export failed:', err)
      toast.error('Failed to export PDF', { id: 'pdf-export' })
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full text-xs"><Clock className="w-3 h-3" />Pending</span>
      case 'processing':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs"><Loader2 className="w-3 h-3 animate-spin" />Processing</span>
      case 'completed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs"><CheckCircle className="w-3 h-3" />Completed</span>
      case 'failed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-xs"><XCircle className="w-3 h-3" />Failed</span>
      default:
        return <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs">{status}</span>
    }
  }

  const truncateText = (text, maxLength = 20) => {
    if (!text) return ''
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

  const handleDeleteResearch = async (id) => {
    if (!confirm('Are you sure you want to delete this research?')) return
    
    try {
      await api.delete(`/gtm/knowledge-base/${id}`)
      setKnowledgeBase(prev => prev.filter(r => r._id !== id))
      toast.success('Research deleted')
    } catch (err) {
      toast.error('Failed to delete research')
    }
  }

  const handleToggleFavorite = async (id) => {
    try {
      const response = await api.patch(`/gtm/knowledge-base/${id}/favorite`)
      setKnowledgeBase(prev => prev.map(r => r._id === id ? response.data : r))
    } catch (err) {
      toast.error('Failed to update favorite')
    }
  }

  const handleViewResearch = (item) => {
    console.log('Viewing research item:', item)
    setSelectedResearch(item)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">GTM Research</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">AI-powered Go-To-Market and competitor analysis</p>
        </div>
        
        {/* Tabs */}
        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('research')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'research'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Sparkles className="w-4 h-4 inline mr-2" />
            New Research
          </button>
          <button
            onClick={() => setActiveTab('knowledge-base')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'knowledge-base'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            Knowledge Base
          </button>
        </div>
      </div>

      {activeTab === 'research' && (
        <>
      {/* Research Form */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6">
        <form onSubmit={handleResearch} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Research Topic
            </label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., AI-powered CRM solutions in healthcare"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Additional Context (Optional)
            </label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Provide any additional context, target market, or specific requirements..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              AI Model
            </label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <optgroup label="🟢 OpenAI - Best Quality">
                <option value="gpt-4o-mini">GPT-4o Mini - Fast & Affordable (~$0.00015/1K tokens)</option>
                <option value="gpt-4o">GPT-4o - Most Capable (~$0.005/1K tokens)</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo - Budget (~$0.0005/1K tokens)</option>
                <option value="o1-mini">O1 Mini - Reasoning (~$0.003/1K tokens)</option>
              </optgroup>
              <optgroup label="🟣 Anthropic Claude - Latest">
                <option value="claude-sonnet-4-20250514">Claude Sonnet 4 - Latest Best (~$0.003/1K in, $0.015/1K out)</option>
                <option value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet - Extended Thinking (~$0.003/1K tokens)</option>
                <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet v2 - Balanced (~$0.003/1K tokens)</option>
                <option value="claude-3-5-haiku-20241022">Claude 3.5 Haiku - Fast & Cheap (~$0.0008/1K tokens)</option>
              </optgroup>
              <optgroup label="🟣 Anthropic Claude - Classic">
                <option value="claude-3-opus-20240229">Claude 3 Opus - Most Powerful (~$0.015/1K tokens)</option>
                <option value="claude-3-sonnet-20240229">Claude 3 Sonnet (~$0.003/1K tokens)</option>
                <option value="claude-3-haiku-20240307">Claude 3 Haiku (~$0.00025/1K tokens)</option>
              </optgroup>
              <optgroup label="🔵 Google Gemini - Free Tier Available">
                <option value="gemini-2.5-flash">Gemini 2.5 Flash - Latest (Free tier: 20/day)</option>
                <option value="gemini-1.5-pro">Gemini 1.5 Pro - Advanced (Free tier: 2/min)</option>
                <option value="gemini-1.5-flash">Gemini 1.5 Flash - Fast (Free tier: 15/min)</option>
              </optgroup>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Select the AI model for research generation. OpenAI and Claude require API keys.
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 font-medium"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {loading ? 'Researching...' : 'Generate Research Report'}
          </button>
        </form>
      </div>

      {/* Example Topics */}
      {!loading && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Example Research Topics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Cloud migration services market in India',
              'AI-powered customer service platforms',
              'Cybersecurity solutions for financial institutions',
              'Digital transformation consulting for manufacturing',
              'IoT solutions for smart cities',
              'Blockchain in supply chain management'
            ].map((example, index) => (
              <button
                key={index}
                onClick={() => setTopic(example)}
                className="text-left px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors text-gray-700 dark:text-gray-300"
              >
                <p className="text-sm">{example}</p>
              </button>
            ))}
          </div>
        </div>
      )}
        </>
      )}

      {/* Knowledge Base Tab - Tabular View */}
      {activeTab === 'knowledge-base' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Research Tasks</h2>
            <button
              onClick={() => fetchKnowledgeBase()}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
          
          {loadingKB ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : knowledgeBase.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
              <Database className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Research Tasks</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">Start a new research task to see results here.</p>
              <button
                onClick={() => setActiveTab('research')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Start Research
              </button>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Topic</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {knowledgeBase.map((item) => (
                      <tr key={item._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {item.isFavorite && <Star className="w-4 h-4 text-yellow-500 fill-current flex-shrink-0" />}
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100" title={item.topic}>
                              {truncateText(item.topic, 30)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(item.status)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {/* View button for completed or failed items */}
                            {(item.status === 'completed' || item.status === 'failed') && (
                              <button
                                onClick={() => handleViewResearch(item)}
                                className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            )}
                            {item.status === 'completed' && (
                              <button
                                onClick={() => handleExportPDF(item)}
                                className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                                title="Export PDF"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            )}
                            {item.status === 'failed' && (
                              <span className="text-xs text-red-500" title={item.error}>
                                {truncateText(item.error, 20)}
                              </span>
                            )}
                            <button
                              onClick={() => handleDeleteResearch(item._id)}
                              className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* View Research Modal - Structured Insights */}
      {selectedResearch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{selectedResearch.topic}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Generated {new Date(selectedResearch.createdAt).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleExportPDF(selectedResearch)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
                <button
                  onClick={() => setSelectedResearch(null)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-8">
              {/* Check if research has any content - handle empty objects */}
              {(() => {
                const hasExecutiveSummary = !!selectedResearch.executiveSummary;
                const hasMarketOverview = selectedResearch.marketOverview && 
                  (typeof selectedResearch.marketOverview === 'string' 
                    ? selectedResearch.marketOverview.length > 0 
                    : Object.keys(selectedResearch.marketOverview).length > 0);
                const hasKeyMetrics = selectedResearch.keyMetrics?.length > 0;
                const hasCompetitors = selectedResearch.competitors?.length > 0;
                const hasOpportunities = selectedResearch.opportunities?.length > 0;
                const hasThreats = selectedResearch.threats?.length > 0;
                const hasRecommendations = selectedResearch.recommendations?.length > 0;
                const hasActionItems = selectedResearch.actionItems?.length > 0;
                
                const hasAnyContent = hasExecutiveSummary || hasMarketOverview || hasKeyMetrics || 
                  hasCompetitors || hasOpportunities || hasThreats || hasRecommendations || hasActionItems;
                
                if (!hasAnyContent) {
                  const isQuotaError = selectedResearch.error?.includes('API_QUOTA_EXCEEDED') || 
                                       selectedResearch.error?.includes('quota') ||
                                       selectedResearch.error?.includes('429');
                  
                  return (
                    <div className="text-center py-12">
                      {isQuotaError ? (
                        <>
                          <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="w-8 h-8 text-orange-500" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">API Quota Exceeded</h3>
                          <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-4">
                            You've reached the Gemini API free tier limit (20 requests/day).
                          </p>
                          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 max-w-md mx-auto">
                            <p className="text-sm text-orange-800 dark:text-orange-200 mb-2">
                              <strong>Options:</strong>
                            </p>
                            <ul className="text-sm text-orange-700 dark:text-orange-300 text-left list-disc list-inside space-y-1">
                              <li>Wait until tomorrow for quota reset</li>
                              <li>Upgrade your plan at <a href="https://ai.google.dev/pricing" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-900">ai.google.dev/pricing</a></li>
                            </ul>
                          </div>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Research Data</h3>
                          <p className="text-gray-500 dark:text-gray-400">This research entry has no content.</p>
                          {selectedResearch.status === 'failed' && selectedResearch.error && (
                            <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 max-w-md mx-auto">
                              <p className="text-sm text-red-600 dark:text-red-400">
                                <strong>Error:</strong> {selectedResearch.error}
                              </p>
                            </div>
                          )}
                          {selectedResearch.status === 'pending' && (
                            <p className="text-yellow-500 mt-2">Status: Pending - waiting to start</p>
                          )}
                          {selectedResearch.status === 'processing' && (
                            <p className="text-blue-500 mt-2">Status: Processing - please wait...</p>
                          )}
                        </>
                      )}
                    </div>
                  );
                }
                return null;
              })()}

              {/* Executive Summary */}
              {selectedResearch.executiveSummary && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Executive Summary
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">{selectedResearch.executiveSummary}</p>
                </div>
              )}

              {/* Key Metrics Grid */}
              {selectedResearch.keyMetrics?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    Key Metrics
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {selectedResearch.keyMetrics.map((m, i) => (
                      <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{m.value}</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">{m.metric}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{m.source}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Market Overview - Handle both old (string) and new (object) format */}
              {selectedResearch.marketOverview && (
                typeof selectedResearch.marketOverview === 'string' 
                  ? selectedResearch.marketOverview.length > 0 
                  : Object.keys(selectedResearch.marketOverview).length > 0
              ) && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    Market Overview
                  </h3>
                  {/* Old format: marketOverview is a string */}
                  {typeof selectedResearch.marketOverview === 'string' ? (
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedResearch.marketOverview}</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        {selectedResearch.marketOverview.marketSize && (
                          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                            <p className="text-xs text-purple-600 dark:text-purple-400 uppercase font-medium">Market Size</p>
                            <p className="text-xl font-bold text-purple-900 dark:text-purple-100">{selectedResearch.marketOverview.marketSize}</p>
                          </div>
                        )}
                        {selectedResearch.marketOverview.growthRate && (
                          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                            <p className="text-xs text-green-600 dark:text-green-400 uppercase font-medium">Growth Rate (CAGR)</p>
                            <p className="text-xl font-bold text-green-900 dark:text-green-100">{selectedResearch.marketOverview.growthRate}</p>
                          </div>
                        )}
                        {selectedResearch.marketOverview.projectedSize && (
                          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                            <p className="text-xs text-blue-600 dark:text-blue-400 uppercase font-medium">Projected Size</p>
                            <p className="text-xl font-bold text-blue-900 dark:text-blue-100">{selectedResearch.marketOverview.projectedSize}</p>
                          </div>
                        )}
                      </div>
                      {selectedResearch.marketOverview.currentState && (
                        <p className="text-gray-700 dark:text-gray-300 mb-4">{selectedResearch.marketOverview.currentState}</p>
                      )}
                      {selectedResearch.marketOverview.keyTrends?.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Key Trends:</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {selectedResearch.marketOverview.keyTrends.map((t, i) => (
                              <div key={i} className="flex items-start gap-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  t.impact === 'High' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                  t.impact === 'Medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                  'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                                }`}>{t.impact}</span>
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-gray-100">{t.trend}</p>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">{t.description}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Competitors */}
              {selectedResearch.competitors?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-red-600" />
                    Competitor Analysis
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedResearch.competitors.map((c, i) => (
                      <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100">{c.name}</h4>
                          {c.marketShare && (
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs font-medium">
                              {c.marketShare} market share
                            </span>
                          )}
                        </div>
                        {c.differentiator && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 italic">"{c.differentiator}"</p>
                        )}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="font-medium text-green-600 dark:text-green-400 mb-1">Strengths</p>
                            <ul className="space-y-1">
                              {c.strengths?.map((s, j) => (
                                <li key={j} className="text-gray-600 dark:text-gray-400 flex items-start gap-1">
                                  <span className="text-green-500">+</span> {s}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="font-medium text-red-600 dark:text-red-400 mb-1">Weaknesses</p>
                            <ul className="space-y-1">
                              {c.weaknesses?.map((w, j) => (
                                <li key={j} className="text-gray-600 dark:text-gray-400 flex items-start gap-1">
                                  <span className="text-red-500">-</span> {w}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Opportunities */}
              {selectedResearch.opportunities?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-green-700 dark:text-green-400 mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" />
                    Opportunities
                  </h3>
                  <div className="space-y-3">
                    {selectedResearch.opportunities.map((o, i) => (
                      <div key={i} className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-green-900 dark:text-green-100">{o.title || o}</h4>
                          {o.priority && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              o.priority === 'High' ? 'bg-red-100 text-red-700' :
                              o.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>{o.priority} Priority</span>
                          )}
                        </div>
                        {o.description && <p className="text-sm text-green-800 dark:text-green-200 mb-2">{o.description}</p>}
                        <div className="flex gap-4 text-xs text-green-700 dark:text-green-300">
                          {o.potentialRevenue && <span>💰 {o.potentialRevenue}</span>}
                          {o.timeToMarket && <span>⏱️ {o.timeToMarket}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Threats */}
              {selectedResearch.threats?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-red-700 dark:text-red-400 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Threats & Risks
                  </h3>
                  <div className="space-y-3">
                    {selectedResearch.threats.map((t, i) => (
                      <div key={i} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-red-900 dark:text-red-100">{t.title || t}</h4>
                          {t.severity && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              t.severity === 'High' ? 'bg-red-200 text-red-800' :
                              t.severity === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>{t.severity} Severity</span>
                          )}
                        </div>
                        {t.description && <p className="text-sm text-red-800 dark:text-red-200 mb-2">{t.description}</p>}
                        {t.mitigation && (
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-2 mt-2">
                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Mitigation:</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{t.mitigation}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Items */}
              {selectedResearch.actionItems?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-orange-700 dark:text-orange-400 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Action Items
                  </h3>
                  <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-orange-100 dark:bg-orange-900/40">
                        <tr>
                          <th className="px-4 py-2 text-left text-orange-800 dark:text-orange-200">Action</th>
                          <th className="px-4 py-2 text-left text-orange-800 dark:text-orange-200">Priority</th>
                          <th className="px-4 py-2 text-left text-orange-800 dark:text-orange-200">Timeline</th>
                          <th className="px-4 py-2 text-left text-orange-800 dark:text-orange-200">Owner</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-orange-200 dark:divide-orange-800">
                        {selectedResearch.actionItems.map((a, i) => (
                          <tr key={i}>
                            <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                              <p className="font-medium">{a.action}</p>
                              {a.expectedOutcome && <p className="text-xs text-gray-500 mt-1">→ {a.expectedOutcome}</p>}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                a.priority === 'High' ? 'bg-red-100 text-red-700' :
                                a.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>{a.priority}</span>
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{a.timeline}</td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{a.owner}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {selectedResearch.recommendations?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-blue-700 dark:text-blue-400 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Strategic Recommendations
                  </h3>
                  <div className="space-y-4">
                    {selectedResearch.recommendations.map((r, i) => (
                      <div key={i} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-blue-900 dark:text-blue-100">{r.title || r}</h4>
                          <div className="flex gap-2">
                            {r.impact && (
                              <span className="px-2 py-0.5 rounded text-xs bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                                Impact: {r.impact}
                              </span>
                            )}
                            {r.effort && (
                              <span className="px-2 py-0.5 rounded text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                Effort: {r.effort}
                              </span>
                            )}
                          </div>
                        </div>
                        {r.description && <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">{r.description}</p>}
                        {r.kpis?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {r.kpis.map((kpi, j) => (
                              <span key={j} className="px-2 py-1 bg-white dark:bg-gray-800 rounded text-xs text-gray-700 dark:text-gray-300 border border-blue-200 dark:border-blue-700">
                                📊 {kpi}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GTMResearch
